import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import {
  regions,
  serviceAreas,
  distanceLabel,
  kindLabel,
  milesFromBase,
} from "../data/serviceAreas";
import { VIEW, COUNTY_PATH, project, UNITS_PER_MILE } from "../data/serviceAreaMap";

// ── Label placement ──────────────────────────────────────────────────────
// Seven West Valley cities sit inside a 75 x 114 unit box, so twenty-eight
// always-on labels cannot be legible at once. Cities with room carry their
// name on the map; the dense ones reveal theirs on hover, focus, search or
// when their region is selected. Every name is always readable in the index
// beside the map, so nothing depends on reading a label.
//   side: which way the name sits from its dot
//   always: true = drawn in the default view
const LABEL = {
  buckeye:          { side: "l",  always: true },
  surprise:         { side: "l",  always: true },
  "sun-city-west":  { side: "l" },
  "el-mirage":      { side: "l" },
  youngtown:        { side: "l" },
  "sun-city":       { side: "r" },
  peoria:           { side: "r",  always: true },
  glendale:         { side: "b",  always: true },
  "litchfield-park":{ side: "l" },
  goodyear:         { side: "l",  always: true },
  avondale:         { side: "b" },
  tolleson:         { side: "t" },
  phoenix:          { side: "b",  always: true },
  guadalupe:        { side: "l" },
  anthem:           { side: "r",  always: true },
  "cave-creek":     { side: "bl", always: true },
  carefree:         { side: "r",  always: true },
  "paradise-valley":{ side: "r" },
  scottsdale:       { side: "r",  always: true },
  "fountain-hills": { side: "r",  always: true },
  tempe:            { side: "t",  always: true },
  mesa:             { side: "r",  always: true },
  gilbert:          { side: "r",  always: true },
  chandler:         { side: "b",  always: true },
  "queen-creek":    { side: "r",  always: true },
  "apache-junction":{ side: "r",  always: true },
};

const OFFSET = { r: [11, 4], l: [-11, 4], t: [0, -11], b: [0, 18],
                 br: [9, 16], bl: [-9, 16], tr: [9, -10], tl: [-9, -10] };
const ANCHOR = { r: "start", l: "end", t: "middle", b: "middle",
                 br: "start", bl: "end", tr: "start", tl: "end" };

// Dot size carries what kind of place it is — the same field the city cards
// print underneath each name.
const DOT = { city: 6.5, town: 5.2, community: 5.2 };
const HUB_DOT = 9.5;

// Rings are the drive bands the city cards already quote ("About 20 miles").
const RINGS = [15, 30, 45];

const norm = (s) => s.toLowerCase().normalize("NFKD").replace(/[^a-z0-9 ]/g, "");

function useCompact() {
  const [compact, setCompact] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const on = (e) => setCompact(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return compact;
}

export default function ServiceAreaMap() {
  const navigate = useNavigate();
  const compact = useCompact();
  const [query, setQuery] = useState("");
  const [region, setRegion] = useState("all");
  const [active, setActive] = useState(null); // slug under cursor / keyboard
  const rowRefs = useRef({});

  const placed = useMemo(
    () =>
      serviceAreas.map((a) => ({
        ...a,
        ...project(a.lat, a.lng),
        miles: milesFromBase(a),
        cfg: LABEL[a.slug] || { side: "r" },
      })),
    []
  );

  const q = norm(query.trim());
  const matches = useMemo(() => {
    return placed.filter((a) => {
      if (region !== "all" && a.region !== region) return false;
      if (!q) return true;
      return (
        norm(a.name).includes(q) ||
        (a.zips || []).some((z) => z.startsWith(q)) ||
        norm(kindLabel(a)).includes(q)
      );
    });
  }, [placed, q, region]);

  const inSet = useMemo(() => new Set(matches.map((a) => a.slug)), [matches]);
  const filtering = region !== "all" || q.length > 0;
  const phoenix = placed.find((a) => a.slug === "phoenix");

  const go = (slug) => navigate(`/service-areas/${slug}`);

  const onMarkerKey = (e, slug) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      go(slug);
    }
  };

  const focusArea = (slug) => {
    setActive(slug);
    const row = rowRefs.current[slug];
    if (row) row.scrollIntoView({ block: "nearest" });
  };

  return (
    <div className="grid lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] gap-6 items-start">
      {/* ── Map ─────────────────────────────────────────────────────── */}
      <figure className="order-2 lg:order-1 relative rounded-2xl overflow-hidden bg-ink ring-1 ring-ink/10 shadow-xl">
        <svg
          viewBox={`0 0 ${VIEW.w} ${VIEW.h}`}
          className="w-full h-auto block"
          role="img"
          aria-label={`Map of Maricopa County, Arizona showing the ${serviceAreas.length} cities and towns Pepla Roofing serves.`}
        >
          <defs>
            <pattern id="pepla-map-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M40 0H0V40" fill="none" stroke="#ffffff" strokeOpacity="0.04" strokeWidth="1" />
            </pattern>
          </defs>

          <rect width={VIEW.w} height={VIEW.h} fill="#0d0d0d" />
          <rect width={VIEW.w} height={VIEW.h} fill="url(#pepla-map-grid)" />

          {/* County */}
          <path d={COUNTY_PATH} fill="#17160f" stroke="#FC3F00" strokeOpacity="0.5" strokeWidth="2.2" strokeLinejoin="round" />

          {/* Drive bands out of Phoenix */}
          <g className="pointer-events-none">
            {RINGS.map((mi) => (
              <g key={mi}>
                <circle
                  cx={phoenix.x}
                  cy={phoenix.y}
                  r={mi * UNITS_PER_MILE}
                  fill="none"
                  stroke="#ffffff"
                  strokeOpacity="0.13"
                  strokeWidth="1"
                  strokeDasharray="3 6"
                />
                {!compact && <text
                  x={phoenix.x - mi * UNITS_PER_MILE * 0.819}
                  y={phoenix.y + mi * UNITS_PER_MILE * 0.574 + 4}
                  textAnchor="middle"
                  className="fill-white/35 font-sans"
                  style={{ fontSize: 11, letterSpacing: "0.08em" }}
                >
                  {mi} mi
                </text>}
              </g>
            ))}
          </g>

          {/* Cities */}
          <g>
            {placed.map((a) => {
              const on = inSet.has(a.slug);
              const isActive = active === a.slug;
              const isHub = a.slug === "phoenix";
              const r = (isHub ? HUB_DOT : DOT[a.kind] || 5.2) * (isActive ? 1.45 : 1) * (compact ? 1.5 : 1);
              const [dx, dy] = OFFSET[a.cfg.side];
              const showLabel = !compact && (isActive || a.cfg.always || (filtering && on));

              return (
                <a
                  key={a.slug}
                  href={`/service-areas/${a.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    go(a.slug);
                  }}
                  onMouseEnter={() => focusArea(a.slug)}
                  onMouseLeave={() => setActive(null)}
                  onFocus={() => focusArea(a.slug)}
                  onBlur={() => setActive(null)}
                  onKeyDown={(e) => onMarkerKey(e, a.slug)}
                  tabIndex={compact ? -1 : 0}
                  aria-hidden={compact ? "true" : undefined}
                  aria-label={`${a.name}, Arizona — ${kindLabel(a)}, ${distanceLabel(a)}. See roofing services in ${a.name}.`}
                  className={`pepla-map-pin outline-none ${compact ? "pointer-events-none" : "cursor-pointer"}`}
                  style={{ opacity: on ? 1 : 0.17 }}
                >
                  {/* Generous invisible hit area — the dots are small. */}
                  <circle cx={a.x} cy={a.y} r="17" fill="transparent" />

                  {isActive && (
                    <circle cx={a.x} cy={a.y} r={r + 7} fill="none" stroke="#FC3F00" strokeWidth="1.6" strokeOpacity="0.85" />
                  )}
                  <circle
                    cx={a.x}
                    cy={a.y}
                    r={r}
                    fill={isHub || isActive ? "#FC3F00" : "#F3E9DD"}
                    stroke={isHub ? "#FFD9C6" : "#0d0d0d"}
                    strokeWidth={isHub ? 2 : 1.4}
                  />
                  {isHub && <circle cx={a.x} cy={a.y} r="3.2" fill="#0d0d0d" />}

                  {showLabel && (
                    <text
                      x={a.x + dx}
                      y={a.y + dy}
                      textAnchor={ANCHOR[a.cfg.side]}
                      className={`font-sans ${isActive ? "fill-white" : isHub ? "fill-white/90" : "fill-white/70"}`}
                      style={{
                        fontSize: isHub ? 16 : 13.5,
                        fontWeight: isHub || isActive ? 600 : 500,
                        paintOrder: "stroke",
                        stroke: "#0d0d0d",
                        strokeWidth: 3.5,
                        strokeLinejoin: "round",
                      }}
                    >
                      {a.name}
                    </text>
                  )}
                </a>
              );
            })}
          </g>
        </svg>

        <figcaption className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-3.5 bg-ink text-[11px] text-white/55 border-t border-white/10">
          <span className="inline-flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-brand ring-2 ring-brand/30" /> Phoenix — where our crews start
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#F3E9DD" }} /> City or town we serve
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="w-5 border-t border-dashed border-white/35" /> Drive distance from Phoenix
          </span>
        </figcaption>
      </figure>

      {/* ── Index ───────────────────────────────────────────────────── */}
      <div className="order-1 lg:order-2 lg:sticky lg:top-24">
        <label htmlFor="area-search" className="sr-only">
          Search for your city or ZIP code
        </label>
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/40" aria-hidden="true" />
          <input
            id="area-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Your city or ZIP code"
            className="w-full h-12 pl-10 pr-10 rounded-xl border border-ink/15 bg-white text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md text-ink/40 hover:text-ink focus:outline-none focus:ring-2 focus:ring-brand"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 mt-3" role="group" aria-label="Filter by part of the Valley">
          {[{ key: "all", name: "All areas" }, ...regions].map((r) => {
            const on = region === r.key;
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => setRegion(r.key)}
                aria-pressed={on}
                className={`text-xs rounded-full px-3 py-1.5 border transition-colors focus:outline-none focus:ring-2 focus:ring-brand ${
                  on
                    ? "bg-ink text-white border-ink"
                    : "border-ink/15 text-ink/65 hover:border-brand hover:text-brand"
                }`}
              >
                {r.name}
              </button>
            );
          })}
        </div>

        <p className="text-xs text-ink/50 mt-3" aria-live="polite">
          {matches.length === 0
            ? "No match in Maricopa County. Call (602) 834-4707 — we may still reach you."
            : `${matches.length} of ${serviceAreas.length} places`}
        </p>

        <ul className="mt-2 max-h-[27rem] overflow-y-auto pr-1 divide-y divide-ink/8 border-y border-ink/10">
          {matches.map((a) => (
            <li key={a.slug}>
              <button
                type="button"
                ref={(el) => {
                  rowRefs.current[a.slug] = el;
                }}
                onClick={() => go(a.slug)}
                onMouseEnter={() => setActive(a.slug)}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(a.slug)}
                onBlur={() => setActive(null)}
                className={`w-full text-left flex items-baseline justify-between gap-3 px-3 py-2.5 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand ${
                  active === a.slug ? "bg-brand/8" : "hover:bg-ink/4"
                }`}
              >
                <span className="min-w-0">
                  <span className="block font-medium text-ink truncate">{a.name}</span>
                  <span className="block text-[11px] text-ink/50 truncate">{kindLabel(a)}</span>
                </span>
                <span className="text-[11px] text-ink/45 whitespace-nowrap">{distanceLabel(a)}</span>
              </button>
            </li>
          ))}
        </ul>

        {matches.length === 0 && (
          <a
            href="tel:+16028344707"
            className="mt-4 inline-flex items-center justify-center w-full h-11 rounded-xl bg-brand text-white font-medium hover:bg-brand-dark transition-colors focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
          >
            Call (602) 834-4707
          </a>
        )}
      </div>
    </div>
  );
}
